import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, Calendar, CreditCard, Bell, CheckCircle2, 
  ChevronRight, Star, Heart, MapPin, Clock,
  ArrowRight, Sparkles, Shield, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const [stats, setStats] = useState({ members: 0, events: 0, committees: 0 });
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    fetchPublicData();
  }, []);

  const fetchPublicData = async () => {
    try {
      // Try to get public stats - this is optional
      const [statsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/stats`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/public/events`).catch(() => ({ data: [] }))
      ]);
      if (statsRes.data) setStats(statsRes.data);
      if (eventsRes.data) setUpcomingEvents(eventsRes.data.slice(0, 3));
    } catch (error) {
      // Public endpoints may not exist, that's ok
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Member Directory',
      description: 'Reconnect with your batchmates. Search by batch year and find old friends.',
      color: 'bg-maroon'
    },
    {
      icon: Bell,
      title: 'Announcements',
      description: 'Stay updated with the latest news. Never miss important reunion updates.',
      color: 'bg-gold'
    },
    {
      icon: Calendar,
      title: 'Event Program',
      description: 'View the complete sequence of events and RSVP to activities.',
      color: 'bg-blue-500'
    },
    {
      icon: CreditCard,
      title: 'Easy Payments',
      description: 'Pay via GCash or bank transfer. Upload proof and track your payments.',
      color: 'bg-green-500'
    },
    {
      icon: FileText,
      title: 'Document Library',
      description: 'Access event photos, meeting minutes, and important documents.',
      color: 'bg-purple-500'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your data is safe with us. Only registered members can access the platform.',
      color: 'bg-orange-500'
    }
  ];

  const testimonials = [
    {
      name: 'Maria Santos',
      batch: '1998',
      text: 'Finally reconnected with my batch mates after 25 years! This platform made organizing our reunion so easy.',
      avatar: 'MS'
    },
    {
      name: 'Juan Dela Cruz',
      batch: '1995',
      text: 'The payment tracking feature is brilliant. No more chasing people for contributions!',
      avatar: 'JD'
    },
    {
      name: 'Ana Reyes',
      batch: '2000',
      text: 'Love how we can see the event program and RSVP to each activity. So organized!',
      avatar: 'AR'
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-maroon flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">b8</span>
              </div>
              <span className="font-heading font-bold text-xl text-maroon">batchm8s</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/organizer/login">
                <Button variant="ghost" className="text-maroon hover:bg-maroon/5" data-testid="nav-organizer-btn">
                  Organizer
                </Button>
              </Link>
              <Link to="/attendee/login">
                <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full px-6" data-testid="nav-attendee-btn">
                  Join Reunion
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-maroon via-maroon-hover to-maroon" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <Badge className="bg-gold text-cream-dark mb-6 text-sm px-4 py-1">
                <Sparkles className="h-4 w-4 mr-2" />
                Reunion Made Easy
              </Badge>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Reconnect with Your
                <span className="text-gold"> Batchmates</span>
              </h1>
              <p className="mt-6 text-lg text-white/80 max-w-lg">
                batchm8s helps you organize your school reunion effortlessly. 
                Manage events, track payments, and reconnect with classmates all in one place.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/attendee/login">
                  <Button 
                    size="lg" 
                    className="bg-gold hover:bg-gold-hover text-cream-dark rounded-full px-8 text-lg shadow-lg hover:shadow-xl transition-all"
                    data-testid="hero-attendee-btn"
                  >
                    Join a Reunion
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/organizer/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 text-lg"
                    data-testid="hero-organizer-btn"
                  >
                    Organize an Event
                  </Button>
                </Link>
              </div>
              
              {/* Quick Stats */}
              <div className="mt-12 flex gap-8">
                <div>
                  <div className="text-3xl font-bold text-gold">500+</div>
                  <div className="text-sm text-white/60">Alumni Registered</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gold">25+</div>
                  <div className="text-sm text-white/60">Batch Years</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gold">10+</div>
                  <div className="text-sm text-white/60">Reunions Organized</div>
                </div>
              </div>
            </div>
            
            {/* Hero Image/Card */}
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gold/20 rounded-3xl blur-xl" />
                <Card className="relative bg-white/10 backdrop-blur-sm border-white/20 p-8 rounded-3xl">
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-maroon" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Grand Reunion 2025</p>
                        <p className="text-white/60 text-sm">December 15, 2025</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                        <CheckCircle2 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Payment Verified</p>
                        <p className="text-white/60 text-sm">Registration: ₱500.00</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">150 Batchmates</p>
                        <p className="text-white/60 text-sm">Already confirmed attendance</p>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="#F9F7F2"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-cream" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-maroon/10 text-maroon mb-4">Features</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-maroon">
              Everything You Need for Your Reunion
            </h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              From planning to execution, batchm8s has all the tools to make your reunion a success.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 bg-white group hover:-translate-y-1"
              >
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-gold/20 text-gold-foreground mb-4">How It Works</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-maroon">
              Get Started in Minutes
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Create or Join', description: 'Organizers create events and get a unique code. Attendees use that code to join.' },
              { step: '2', title: 'Connect', description: 'Find your batchmates, get announcements, and stay updated.' },
              { step: '3', title: 'Attend', description: 'Pay your registration, RSVP to events, and enjoy the reunion!' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-maroon text-white font-heading text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
                {index < 2 && (
                  <ChevronRight className="h-8 w-8 text-gold mx-auto mt-4 hidden md:block rotate-0 md:rotate-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-28 bg-cream-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-maroon/10 text-maroon mb-4">Testimonials</Badge>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-maroon">
              What Our Alumni Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-sm bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                    ))}
                  </div>
                  <p className="text-gray-600 italic mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-maroon flex items-center justify-center text-white font-medium">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">Batch {testimonial.batch}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-maroon relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="h-16 w-16 text-gold mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Reconnect?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join hundreds of alumni who have already reunited with their batchmates. 
            Your school memories are waiting to be relived.
          </p>
          <Link to="/attendee/login">
            <Button 
              size="lg" 
              className="bg-gold hover:bg-gold-hover text-cream-dark rounded-full px-10 text-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="cta-attendee-btn"
            >
              Join a Reunion
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-maroon flex items-center justify-center">
                  <span className="text-white font-heading font-bold text-lg">b8</span>
                </div>
                <span className="font-heading font-bold text-xl">batchm8s</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Reconnecting alumni, one reunion at a time. 
                Because the best memories are the ones we share.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/organizer/login" className="hover:text-white transition-colors">Organizer Portal</Link></li>
                <li><Link to="/attendee/login" className="hover:text-white transition-colors">Join a Reunion</Link></li>
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>info@batchm8s.com</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-gray-500 text-sm">© 2026 batchm8s. Made with ❤️ for alumni everywhere.</p>
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/cookies" className="hover:text-white transition-colors">Cookies Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
